import { useLayoutEffect } from 'react'

/**
 * POZADINSKI TEMELJ (Z15-polir §1) — POVRŠINA, ne tema (V1A).
 *
 * Do sada su `html` i `body` bili PAPIRNATI (`--paper`), a "luxury noir" svijet
 * gosta i vozača samo kutija povrh njih. U pregledniku se to ne vidi; u
 * instaliranoj aplikaciji na iPhoneu vidi se stalno:
 *
 * · **overscroll** — prst povuče popis preko ruba i iza tamnog ekrana bljesne
 *   krem, gore i dolje, na svakom listanju;
 * · **prijelaz** — dok se dohvaća lazy paket sučelja ili se mijenja ruta, na
 *   trenutak se vidi podloga dokumenta, a ne podloga ekrana;
 * · **traka stanja** — uz `black-translucent` su ikone (sat, baterija, mreža)
 *   BIJELE i crtaju se preko naše podloge; nad krem podlogom nestanu.
 *
 * Lijek nije još jedna kutija nego temelj: prekidač nosi `<html>`, pa je podloga
 * ista prije prvog renderiranja, između dva ekrana i iza ruba zaslona. Zadana je
 * TAMNA (i u `index.html`, prije nego se CSS uopće dohvati): gost i vozač su
 * mobilna publika koja aplikaciju drži instaliranu, a konzola je desktop kojem
 * kratak tamni bljesak ne smeta ni približno koliko krem bljesak smeta ekranu
 * koji je cijeli taman.
 *
 * **Zašto se od V1A zove POVRŠINA.** Atribut je dosad stajao na `data-tema`, a
 * značio je nešto drugo: `noir` je ljuska gosta i vozača, `paper` je radna ploha
 * konzole — dakle KOJA aplikacija stoji na ekranu, a ne kako je resort odlučio da
 * izgleda. Tema je postala vlastita os (`app/vizura.ts`, `data-tema="noir |
 * svijetla"`), pa je ova morala dobiti svoje ime; dva prekidača s istim imenom
 * bila bi zamka za prvoga tko ih vidi zajedno.
 *
 * Boje ostaju u tokenima (CLAUDE.md §17) — ovdje se prebacuje samo prekidač,
 * a što on znači piše u `styles/index.css`.
 */

export type Povrsina = 'noir' | 'paper'

/** Podloga dokumenta kad nijedno sučelje ne kaže drukčije (vidi `index.html`). */
export const ZADANA_POVRSINA: Povrsina = 'noir'

/** Prekidač je atribut na `<html>`; CSS iz njega izvodi podlogu i boju teksta. */
export function primijeniPovrsinu(povrsina: Povrsina): void {
  const korijen = globalThis.document?.documentElement

  if (korijen) korijen.dataset.povrsina = povrsina
}

/**
 * Sučelje objavljuje svoju površinu dok je na ekranu.
 *
 * Pri odlasku se vraća ZADANA, a ne "prethodna": stanje o kojem bi se moralo
 * voditi računovodstvo raziđe se prvim preskočenim odmontiranjem, a zadana je
 * ionako ono što stoji u `index.html` prije nego se ijedno sučelje podigne.
 */
export function usePovrsina(povrsina: Povrsina): void {
  useLayoutEffect(() => {
    primijeniPovrsinu(povrsina)

    return () => primijeniPovrsinu(ZADANA_POVRSINA)
  }, [povrsina])
}

/**
 * Mobilna ljuska (gost, vozač): dokument se NE pomiče (Z15-polir §6).
 *
 * Ljuska je visoka točno jedan zaslon (`h-dvh`) i skrola se samo platno u njoj,
 * pa dokument ispod nje nema što pomaknuti — ima samo što otkriti. Ova oznaka
 * pali pravila u `styles/index.css` koja to zatvaraju s tri strane: elastično
 * odmicanje (`overscroll-behavior-y`), vodoravno pomicanje (`overflow-x`) i sve
 * geste osim okomitog povlačenja (`touch-action: pan-y`). Aplikacija tako
 * "sjedne" u zaslon umjesto da pluta.
 *
 * Stoji odvojeno od teme, iako se uvijek pojavljuju zajedno: ulazni ekrani gosta
 * (prijava, registracija, čekanje) su tamni kao i ljuska, ali se skrolaju kao
 * obična stranica — zaključati dokument ondje značilo bi odrezati dno obrasca na
 * niskom ekranu s otvorenom tipkovnicom.
 */
export function useLjuskaBezPomaka(): void {
  useLayoutEffect(() => {
    const korijen = globalThis.document?.documentElement
    if (!korijen) return

    korijen.dataset.ljuska = 'app'

    return () => {
      delete korijen.dataset.ljuska
    }
  }, [])
}
